import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { supabaseMonitor } from '../utils/supabaseMonitor'
import { config } from '../config/env'
import {
  clearAuthRedirectDestination,
  consumeAuthRedirectDestination,
  DEFAULT_AUTH_DESTINATION,
  oauthErrorMessage,
  readAuthRedirectDestination,
  storeAuthRedirectDestination,
} from '../lib/authRedirect'
import { getUsableSessionAccessToken } from '../lib/sessionAccessToken'
import { clearLandingSourceIntent } from '../lib/landingSourceIntent'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  isSigningOut: boolean
  signInWithGoogle: () => Promise<{ error: any }>
  signOut: () => Promise<void>
  setNavigate: (navigate: (path: string, options?: any) => void) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)
export const AUTH_INITIALIZATION_TIMEOUT_MS = 8_000

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const navigateRef = useRef<((path: string, options?: any) => void) | null>(null)
  const pendingNavigationRef = useRef<string | null>(null)

  const navigateTo = useCallback((destination: string) => {
    if (navigateRef.current) {
      navigateRef.current(destination, { replace: true })
      return
    }
    pendingNavigationRef.current = destination
  }, [])

  const restorePendingDestination = useCallback(() => {
    // A previously stored session is not proof that this recovery attempt
    // succeeded. Leave explicit sign-in and OAuth errors on their recovery UI.
    if (window.location.pathname === '/sign-in' ||
        oauthErrorMessage(window.location.search, window.location.hash)) return
    const destination = consumeAuthRedirectDestination()
    if (destination) {
      navigateTo(destination)
    }
  }, [navigateTo])

  const refreshUsageCache = useCallback((authenticatedSession: Session) => {
    const accessToken = getUsableSessionAccessToken(authenticatedSession)
    if (!authenticatedSession.user?.id || !accessToken) return

    void fetch(config.endpoints.refreshCache(authenticatedSession.user.id), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    }).catch(() => {
      // UsageProvider owns the customer-visible retry state.
    })
  }, [])

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }

    let active = true
    const initializationTimeout = window.setTimeout(() => {
      if (active) setLoading(false)
    }, AUTH_INITIALIZATION_TIMEOUT_MS)

    const applySession = (
      nextSession: Session | null,
      restoreDestination: boolean
    ) => {
      if (!active) return
      window.clearTimeout(initializationTimeout)
      setSession(nextSession)
      setUser(nextSession?.user ?? null)
      setLoading(false)
      if (nextSession && restoreDestination) {
        restorePendingDestination()
      }
    }

    supabaseMonitor.logRequest('AUTH', 'getSession() - initial session check')
    
    void supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        if (active) setLoading(false)
        return
      }

      applySession(session, Boolean(session))
    }).catch(() => {
      if (active) setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        supabaseMonitor.logRequest('AUTH', `onAuthStateChange - ${event}`)
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          applySession(nextSession, event === 'SIGNED_IN')
          if (event === 'SIGNED_IN' && nextSession) {
            refreshUsageCache(nextSession)
          }
        } else if (event === 'SIGNED_OUT') {
          applySession(null, false)
          // Reset signing out flag if it's still set from our manual signOut call
          window.setTimeout(() => {
            setIsSigningOut(false)
          }, 100)
        } else {
          applySession(nextSession, event === 'INITIAL_SESSION' && Boolean(nextSession))
        }
      }
    )

    return () => {
      active = false
      window.clearTimeout(initializationTimeout)
      subscription.unsubscribe()
    }
  }, [refreshUsageCache, restorePendingDestination])

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) {
      const error = new Error('Authentication service not available. Please check your connection.')
      return { error }
    }

    supabaseMonitor.logRequest('AUTH', 'signInWithOAuth - Google')

    if (!readAuthRedirectDestination()) {
      storeAuthRedirectDestination(DEFAULT_AUTH_DESTINATION)
    }
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: process.env.NODE_ENV === 'production' 
            ? window.location.origin 
            : 'http://localhost:3000'
        }
      })

      return { error }
    } catch (err) {
      return { error: err }
    }
  }, [])

  const clearClientAuthState = useCallback(() => {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') && key.includes('auth-token')) {
        localStorage.removeItem(key)
      }
    })
    localStorage.removeItem('supabase.auth.token')
    localStorage.removeItem('clipforge_session_id')
    clearAuthRedirectDestination()
    clearLandingSourceIntent()
    pendingNavigationRef.current = null
    setSession(null)
    setUser(null)
  }, [])

  const signOut = useCallback(async () => {
    try {
      setIsSigningOut(true)
      clearClientAuthState()
      
      supabaseMonitor.logRequest('AUTH', 'signOut')
      await supabase.auth.signOut()
      
      // Redirect to home page using React Router
      if (navigateRef.current) {
        navigateRef.current('/', { replace: true })
      }
      
      // Keep isSigningOut true for a bit longer to prevent ProtectedRoute race condition
      window.setTimeout(() => {
        setIsSigningOut(false)
      }, 100)
    } catch {
      clearClientAuthState()
      if (navigateRef.current) {
        navigateRef.current('/', { replace: true })
      }
      window.setTimeout(() => {
        setIsSigningOut(false)
      }, 100)
    }
  }, [clearClientAuthState])

  const setNavigate = useCallback((navigateFn: (path: string, options?: any) => void) => {
    navigateRef.current = navigateFn
    const pendingDestination = pendingNavigationRef.current
    if (pendingDestination) {
      pendingNavigationRef.current = null
      navigateFn(pendingDestination, { replace: true })
    }
  }, [])

  const value = {
    user,
    session,
    loading,
    isSigningOut,
    signInWithGoogle,
    signOut,
    setNavigate
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
